import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Box, Flex } from "@nubeteck/components";
import { Header, MobileMenu } from "../../layout";
import { MainContainer } from "./main-layout.styled";
import {
  useGetIsTransferActiveMutation,
  useGetUserTeacherInfoMutation,
  useGetPeriodsWithoutRulesMessageQuery,
  useGetUserInfoMutation,
} from "src/services";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/core/store/store";
import { setUserInfo, setIsATeacher, setIsAdmin } from "src/core/store/slices/userSlice";
import {
  decodeJwt,
  getIsUserImpersonated,
  getRolesByToken,
  getRolesByTokenImpersonated,
} from "src/utils";
import { Alert, Button, Skeleton } from "antd";
import { MenuProps } from "antd/lib";
import { useMediaQuery } from "src/hooks";
import { IMenuItem } from "src/Interfaces/options";
import { getStudentMenuItems, getTeacherMenuItems } from "src/constants";
import { getHomeAutoservicioPage, getNavbarAutoservicePage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../../pages/StrapiError";

// Helper function to create content with fallback
const createContent = (content: string, fallback?: string) => {
  return content || fallback || "";
};

const MainLayout = () => {
  const [isMenuMobileVisible, setMenuMobileVisible] = React.useState(false);
  const [getUserInfo, { data: userInfo }] = useGetUserInfoMutation();
  const { userInfo: userInfoActual } = useSelector((state: RootState) => state.user);
  const [getTeacherUserInfo, { data: teacherUserInfo }] = useGetUserTeacherInfoMutation();
  const periodsWithoutRulesMessageInfo = useGetPeriodsWithoutRulesMessageQuery("", {
    refetchOnMountOrArgChange: true,
  });
  const { locale } = useLocale();
  const [homeAutoservicioPage, setHomeAutoservicioPage] = useState<any>(null);
  const [navbarPage, setNavbarPage] = useState<any>(null);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 1180px)");
  const [getIsTransferActive, { data: isTransferActive }] = useGetIsTransferActiveMutation();

  const matriculaAdministrativa = localStorage.getItem("matriculaAdministrativa") === "true";
  const token = localStorage.getItem("token");
  const roles = matriculaAdministrativa
    ? getRolesByTokenImpersonated(token ?? "")
    : getRolesByToken(token ?? "");
  const isAdmin =
    roles.includes("administrador(a) del sistema") ||
    roles.includes("cajero") ||
    roles.includes("vicerrectoria") ||
    roles.includes("retiro administrativo") ||
    roles.includes("perfil financiero") ||
    roles.includes("matricula administrativa") ||
    roles.includes("readonly");
  matriculaAdministrativa;
  const isUserImpersonated = getIsUserImpersonated(localStorage.getItem("token") ?? "");

  const isATeacher = useMemo(() => roles.includes("profesor"), [roles]);

  const profiles = localStorage.getItem("profiles")
    ? JSON.parse(localStorage.getItem("profiles") ?? "")
    : {};
  const rolesItems: MenuProps["items"] = Object?.keys(profiles)?.map((pro) => ({
    key: pro,
    label: pro,
    onClick: () => {
      localStorage.setItem("token", profiles[pro]);
      navigate("/");
      window.location.reload();
    },
  }));

  const userImpersonated: MutableRefObject<any> = useRef(null);

  const adminMenuItems: (IMenuItem | null)[] = useMemo(() => [
    {
      name: createContent(navbarPage?.home_admin, "Inicio"),
      link: "/",
      icon: "",
      roles: [
        "cajero",
        "administrador(a) del sistema",
        "perfil financiero",
        "vicerrectoria",
        "matricula administrativa",
        "retiro administrativo",
        "readonly",
      ],
      tooltip: createContent(navbarPage?.home_admin, "Inicio"),
    },
    {
      name: createContent(navbarPage?.sponsors_admin, "Patrocinios"),
      link: "/admin/sponsorships",
      icon: "",
      roles: ["perfil financiero","readonly"],
      tooltip: createContent(navbarPage?.sponsors_admin, "Patrocinios"),
    },
    isTransferActive?.data
      ? {
          name: "Revisiones",
          link: "/admin/reviews",
          icon: "",
          roles: ["revisor de retiros", "administrador(a) del sistema"],
          tooltip: "Revisiones",
        }
      : null,
    {
      name: createContent(navbarPage?.import_admin, "Importación"),
      link: "/admin/data-imports",
      icon: "",
      roles: ["vicerrectoria"],
      tooltip: createContent(navbarPage?.import_admin, "Importaciones de datos"),
    },
    {
      name: createContent(navbarPage?.box_admin, "Caja"),
      link: "/admin/charges",
      icon: "",
      roles: ["cajero"],
      tooltip: createContent(navbarPage?.box_admin, "Caja administrativa"),
    },
    {
      name: createContent(navbarPage?.setting_admin, "Configuración"),
      link: "/admin/settings",
      icon: "",
      roles: ["administrador(a) del sistema"],
      tooltip: createContent(navbarPage?.setting_admin, "Configuraciones"),
    },
    {
      name: createContent(navbarPage?.loginas_admin, "Ingresar como"),
      link: "admin/user-impersonation",
      icon: "",
      roles: ["administrador(a) del sistema"],
      tooltip: createContent(navbarPage?.logins_admin, "Ingresar como usuario estudiante"),
    },
    {
      name: createContent(navbarPage?.loginas_stud, "Ingreso prof."),
      link: "admin/teacher-impersonation",
      icon: "",
      roles: ["director de escuela"],
      tooltip: createContent(navbarPage?.logins_stud, "Ingresar como usuario docente"),
    },
    {
      name: createContent(navbarPage?.payment, "Pagos"),
      link: "admin/payments-comparison",
      icon: "",
      roles: ["perfil financiero"],
      tooltip: createContent(navbarPage?.payment, "Comparaciones de pagos"),
    },
    {
      name: createContent(navbarPage?.prenrolled_admin, "Pre-matrícula"),
      link: "admin/user-selection",
      icon: "",
      roles: ["matricula administrativa"],
      tooltip: createContent(navbarPage?.prenrolled_admin, "Pre-matrícula"),
    },
    {
      name: createContent(navbarPage?.withdraw_adm, "Retiro adm"),
      link: "/admin/subjects-withdraw",
      icon: "",
      roles: ["retiro administrativo"],
      tooltip: createContent(navbarPage?.withdraw_adm, "Retiro administrativo"),
    },
    {
      name: createContent(navbarPage?.translations_admin, "Traducciones"),
      link: "/admin/translations",
      icon: " ",
      roles: ["administrador(a) del sistema"],
      tooltip: createContent(navbarPage?.translations_admin_tooltip, "Gestión de traducciones"),
    },
  ], [navbarPage?.home_admin, navbarPage?.sponsors_admin, navbarPage?.import_admin, navbarPage?.box_admin, navbarPage?.setting_admin, navbarPage?.loginas_admin, navbarPage?.logins_admin, navbarPage?.loginas_stud, navbarPage?.logins_stud, navbarPage?.payment, navbarPage?.prenrolled_admin, navbarPage?.withdraw_adm, navbarPage?.translations_admin, navbarPage?.translations_admin_tooltip, isTransferActive]);
  useEffect(() => {
    dispatch(setIsATeacher(isATeacher));
    dispatch(setIsAdmin(isAdmin));
  }, [dispatch, isATeacher, isAdmin]);

  useEffect(() => {
    getTeacherUserInfo("");
    getUserInfo("");
  }, [getUserInfo, getTeacherUserInfo]);

  useEffect(() => {
    if (teacherUserInfo) {
      dispatch(setUserInfo({ ...userInfo?.data, ...teacherUserInfo }));
    } else if (isAdmin && userInfo?.data) {
      dispatch(
        setUserInfo({ ...userInfoActual, hayProcesoAbierto: userInfo?.data?.hayProcesoAbierto }),
      );
    } else if (userInfo?.data) {
      getIsTransferActive();

      dispatch(setUserInfo(userInfo?.data));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIsTransferActive, isATeacher, isAdmin, userInfo, teacherUserInfo]);

  useEffect(() => {
    const tokenT = localStorage.getItem("token");
    if (!localStorage.getItem("token")) {
      navigate("/login");
    }
    userImpersonated.current = getIsUserImpersonated(tokenT ?? "");
  }, [navigate]);

  const getUserName = () => {
    return matriculaAdministrativa && userImpersonated.current
      ? JSON.parse(decodeJwt(userImpersonated.current)).name
      : userInfo?.data?.fullName;
  };

  const getMenuByAdminRoles = useMemo(() => {
    return (menuItems: (IMenuItem | null)[]) => {
      const result = menuItems?.filter((menu) => menu?.roles?.find((rol) => roles?.includes(rol)));
      return result;
    };
  }, [roles]);

  const studentMenuItems = useMemo(() => 
    getStudentMenuItems(navbarPage, false), 
    [navbarPage]
  );

  const teacherMenuItems = useMemo(() => 
    getTeacherMenuItems(navbarPage, false), 
    [navbarPage]
  );

  const logoutImpersonated = () => {
    localStorage.setItem("token", userImpersonated.current);
    localStorage.setItem("matriculaAdministrativa", "false");
    localStorage.setItem("refreshToken", JSON.stringify(userImpersonated.current.refreshToken));
    getUserInfo("");
    navigate("/");
    window.location.reload();
  };

  const logout = () => {
    localStorage.setItem("matriculaAdministrativa", "false");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsContentLoading(true);

    // Red de seguridad: si las peticiones se quedan colgadas, deja de mostrar
    // el spinner. Solo apagar el loading, nunca forzar el error de traducciones
    // aquí, porque "todavía no responde" no es lo mismo que "falló".
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setIsContentLoading(false);
      }
    }, 10000);

    // Manejar cada promesa por separado para mejor control de errores
    let completed = 0;
    let failed = 0;
    const total = 2;

    const checkCompletion = (didFail: boolean) => {
      completed++;
      if (didFail) failed++;
      if (completed === total) {
        clearTimeout(timeoutId);
        if (!cancel) {
          setIsContentLoading(false);
          // Solo es un error real de traducciones si AMBAS peticiones a
          // Strapi fallaron; una petición lenta o un error 403 en otro
          // servicio (Admins/DocentesApi) no debe disparar este mensaje.
          setHasError(locale === 'en' && failed === total);
        }
      }
    };

    getHomeAutoservicioPage(locale)
      .then((homeRes) => {
        if (!cancel) {
          const homeData = homeRes?.data?.attributes || homeRes?.data || homeRes;
          setHomeAutoservicioPage(homeData);
          checkCompletion(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          setHomeAutoservicioPage(null);
          checkCompletion(true);
        }
      });

    getNavbarAutoservicePage(locale)
      .then((navbarRes) => {
        if (!cancel) {
          const navbarData = navbarRes?.data?.attributes || navbarRes?.data || navbarRes;
          setNavbarPage(navbarData);
          checkCompletion(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          setNavbarPage(null);
          checkCompletion(true);
        }
      });

    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsContentLoading(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsContentLoading(false);
        setHomeAutoservicioPage(null);
        setNavbarPage(null);
      }}
    />;
  }

  return (
    <Box>
      {isContentLoading ? (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: "#fff",
              padding: "16px",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Skeleton.Input active size="large" style={{ width: 200, marginBottom: 8 }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Skeleton.Input active size="default" style={{ width: 150 }} />
              <div style={{ display: "flex", gap: "8px" }}>
                <Skeleton.Input active size="default" style={{ width: 100 }} />
                <Skeleton.Input active size="default" style={{ width: 80 }} />
              </div>
            </div>
          </div>
          <div style={{ marginTop: "80px", padding: "20px" }}>
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        </>
      ) : (
        <>
          <MobileMenu
            isFakeSelecting={matriculaAdministrativa}
            logoutImpersonated={logoutImpersonated}
            menuItems={
              isATeacher
                ? teacherMenuItems
                : isAdmin
                ? getMenuByAdminRoles(adminMenuItems)
                : studentMenuItems
            }
            userName={getUserName()}
            logout={logout}
            isHelpVisible={!isAdmin}
            isVisible={isMenuMobileVisible}
            hide={() => {
              setMenuMobileVisible(false);
            }}
            profiles={
              localStorage.getItem("profiles")
                ? JSON.parse(localStorage.getItem("profiles") ?? "{}")
                : {}
            }
            isATeacher={isATeacher}
            isAdmin={isAdmin}
          />
          <Header
            isFakeSelecting={matriculaAdministrativa}
            logoutImpersonated={logoutImpersonated}
            menuItems={
              isATeacher
                ? teacherMenuItems
                : isAdmin
                ? getMenuByAdminRoles(adminMenuItems)
                : studentMenuItems
            }
            userName={getUserName()}
            isHelpVisible={!isAdmin}
            logout={logout}
            openMenu={() => {
              setMenuMobileVisible(true);
            }}
          />
          {!isUserImpersonated && !!periodsWithoutRulesMessageInfo.data?.data ? (
            <div style={{ marginTop: isMobile ? 80 : 60, marginBottom: -80, height: 50 }}>
              <Alert
                message={periodsWithoutRulesMessageInfo.data?.data}
                type="error"
                style={{ position: "fixed", minWidth: "100%", zIndex: 1000 }}
              />
            </div>
          ) : null}
          <MainContainer
            style={{ paddingTop: isMobile && userImpersonated.current ? "250px" : "auto" }}
          >
            {userImpersonated.current ? (
              <>
                <br />
                <Flex
                  style={{
                    display: "flex",
                    alignItems: isMobile ? "start" : "center",
                    gap: isMobile ? "7px" : "15px",
                    flexDirection: isMobile ? "column" : "row",
                    backgroundColor: "#c9e1ff",
                    position: "absolute",
                    top: isMobile ? "80px" : "63px",
                    left: 0,
                    width: "100%",
                    justifyContent: "center",
                    padding: isMobile ? "10px" : "0px",
                  }}
                >
                  <Alert
                    message={
                      matriculaAdministrativa
                        ? `${homeAutoservicioPage?.enrolle_stu || "Matrícula del estudiante"} ${userInfo?.data?.fullName} - ${userInfo?.data?.id}`
                        : `${homeAutoservicioPage?.messageO || "El usuario del "} ${userInfo?.data?.id ? homeAutoservicioPage?.student || "estudiante" : homeAutoservicioPage?.docent || "docente"} ${userInfo
                            ?.data.fullName} - ${userInfo?.data
                            .id} ${homeAutoservicioPage?.messageT || "está siendo utilizado por"} ${JSON.parse(
                            decodeJwt(userImpersonated.current),
                          )?.name}`
                    }
                    type="info"
                    style={{ backgroundColor: "transparent", border: "none" }}
                    showIcon
                  />
                  <Button onClick={logoutImpersonated}>
                    {matriculaAdministrativa
                    ? homeAutoservicioPage?.before_button || "Volver a tu usuario" : homeAutoservicioPage?.before_button}
                  </Button>
                </Flex>
              </>
            ) : null}
            <Outlet />
          </MainContainer>
        </>
      )}
    </Box>
  );
};

export default MainLayout;
